import React from "react";
import useRepairIssueInfo from "../store/useRepairIssueInfo";
import useNoiseInfo from "../store/useNoiseInfo";
import TextareaBox from "../../../components/TextareaBox";
import Tag from "../../../components/Tag";
import { NOISE_SUB_CODE, shouldShowLastIssueTime } from "../config/constants";
import { RepairSubItem } from "../interfaces/home";
import DateTimeField from "../components/DateTimeField";
import { isIssueDescriptionComplete } from "../utils/issueValidation";
import { NoiseFeed } from "./NoiseFeed";
import styles from "./index.module.scss";

interface RepairIssueDescriptionProps {
  /** 已选中的故障维修子项（按展示顺序），逐项渲染一条问题描述 */
  subItems: RepairSubItem[];
  /** 上一步（回到预约项目）；只读态不需要 */
  onPrev?: () => void;
  /** 校验通过后进入下一步（回预约主流程「选择门店时间」）；只读态不需要 */
  onNext?: () => void;
  /**
   * 只读态：仅展示已填写的问题描述 / 附件 / 最近一次问题时间 / 异响反馈，
   * 隐藏底部「上一步/下一步」按钮、必填标记与填写引导（供预约详情「查看」复用同一组件）。
   */
  readOnly?: boolean;
}

/**
 * 故障维修「问题描述」——内联全屏阶段（由 homepage/EditingPage 渲染，不走独立路由）。
 * 每个选中的维修子项一条：问题描述（必填）+ 附件上传（选填）+ 最近一次问题时间。
 * - 最近一次问题时间：除异响103/外观内饰104 外的故障维修二级项（含其他108）均露出且必填
 *   （见 shouldShowLastIssueTime）。
 * - 异响子项（code=103）由异响子模块 NoiseFeed（编辑态 Edit / 只读态 Detail）渲染异响专属字段（不含最近一次问题时间）。
 * - 「其他」子项（code=108）与普通子项一致：问题描述（必填）+ 附件（选填）+ 最近一次问题时间（必填）。
 * 描述文本 / 附件 / 最近一次问题时间存于 useRepairIssueInfo；异响专属字段存于 useNoiseInfo。
 */
export default function RepairIssueDescription(
  props: RepairIssueDescriptionProps
) {
  const { subItems, onPrev, onNext, readOnly = false } = props;
  const { getIssue, setIssueDesc, setIssueAttachments, setIssueLastIssueTime } =
    useRepairIssueInfo();
  const { noiseInfo } = useNoiseInfo();

  // 全部故障维修子项均展示问题描述反馈（含「其他」108）；
  // 异响反馈项（code=103）若存在则置顶展示（编辑态/详情只读态一致），其余项保持原相对顺序（稳定排序）
  const feedbackItems = subItems
    .slice()
    .sort(
      (a, b) =>
        (Number(a.code) === NOISE_SUB_CODE ? 0 : 1) -
        (Number(b.code) === NOISE_SUB_CODE ? 0 : 1)
    );

  // 异响是否被选中（校验规则见 utils/issueValidation）
  const isNoiseSelected = feedbackItems.some(
    (s) => Number(s.code) === NOISE_SUB_CODE
  );

  // 必填项是否全部满足（决定「下一步」是否可点，未填齐则按钮置灰不可点）
  const canNext = isIssueDescriptionComplete({
    feedbackItems,
    getIssue,
    isNoiseSelected,
    noiseInfo,
  });

  const handleNext = () => {
    if (!canNext) return;
    onNext?.();
  };

  return (
    <div className={`${styles.page}${readOnly ? " " + styles.pageRo : ""}`}>
      <div className={styles.card}>
        {/* 顶部说明：问题描述（必填） + 橙色提示（只读态不展示引导/必填） */}
        <div className={styles.topTitleRow}>
          <span className={styles.topTitle}>问题描述</span>
          {!readOnly && <Tag type="success">必填</Tag>}
        </div>
        {!readOnly && (
          <div className={styles.topTip}>
            请详细描述需维修的项目问题，可上传照片/视频，我们将提前诊断及备件，以减少您到店的等待时长。
          </div>
        )}

        {feedbackItems.map((item, index) => {
          const code = Number(item.code);
          const isNoise = code === NOISE_SUB_CODE;
          // 最近一次问题时间：除异响103/外观内饰104/其他108 外均露出且必填
          const showLastIssueTime = shouldShowLastIssueTime(code);
          const issue = getIssue(item.code);
          return (
            <div key={item.code} className={styles.issueBlock}>
              <div className={styles.issueTitle}>
                {index + 1}. {item.name}问题描述
              </div>

              <TextareaBox
                placeholder={`请描述您遇到的${item.name}问题`}
                value={issue.desc}
                onChange={(v) => setIssueDesc(item.code, v)}
                maxLength={500}
                readOnly={readOnly}
                required={!readOnly}
                enableVoiceInput={true}
                enableAttachment={true}
                attachmentValue={issue.attachmentIds}
                onAttachmentChange={(ids) =>
                  setIssueAttachments(item.code, ids)
                }
              />

              {/* 最近一次问题时间：按 code 规则露出即必填；只读态无值时不展示 */}
              {showLastIssueTime && (!readOnly || issue.lastIssueTime) && (
                <div className={styles.lastIssueTimeRow}>
                  <DateTimeField
                    label="最近一次问题时间"
                    value={issue.lastIssueTime}
                    allowUnknown
                    onChange={(v) => setIssueLastIssueTime(item.code, v)}
                    readOnly={readOnly}
                    required={!readOnly}
                  />
                </div>
              )}

              {/* 异响子项：同一组件，readOnly 切换编辑/禁用态 */}
              {isNoise && <NoiseFeed readOnly={readOnly} />}
            </div>
          );
        })}
      </div>

      {!readOnly && (
        <div className={styles.bottomBar}>
          <div className={styles.preBtn} onClick={() => onPrev?.()}>
            上一步
          </div>
          <div
            className={`${styles.nextBtn}${
              canNext ? "" : " " + styles.nextBtnDisabled
            }`}
            onClick={handleNext}
          >
            下一步
          </div>
        </div>
      )}
    </div>
  );
}
